import { createApi } from '@reduxjs/toolkit/query/react';
import { LiquidarInformesArgs } from '@/components/Modals/ModalLiquidarInformes';
import { GenericResponse, IInforme, IUser } from '@/redux/models';
import customFetchBase from './customFetchBase';


const excludedKeys = ['id', 'idCliente', 'keyprecio', 'tipo',
  'keyPrecio', 'archivosAdjuntos', 'fechaDeCreacion',
  'expiracionDeRecuperacion', 'fechaEntregadoGobierno', 'informeEntregadoGobierno',
  'liquidado', 'pagado', 'descartado', 'estadoGeneral'];

const filterKeys = (dict: any) => {
  // si tiene exactamente el mismo key que eexcludedKeys lo tenes que sacar del objeto
  // si no tiene el mismo key, lo tenes que dejar

  const keys = Object.keys(dict);
  const filteredKeys = keys.filter((key) => !excludedKeys.includes(key));
  const filteredDict = filteredKeys.reduce((acc, key) => ({ ...acc, [key]: dict[key] }), {});
  return filteredDict;
}


export interface IInformeLog {
  logId: number;
  exceptionTitle: string;
  exceptionMessage: string;
  usuarioId: number;
  usuarioNombre: string;
  usuarioApellido: string;
  usuarioRole: string;
  usuarioEmail: string;
  usuarioCuit: string;
  usuarioTelefono: string;
  usuarioEmpresa: string;
  createdAt: string;
}


interface IQueryCarritos {
  page: number;
  limit: number;
  companyId: number;
  userId: number;
}

export interface FiltersCarpeta {
  page: number;
  limit: number;
  estado?: string;
  userId?: number;
  fechaDesde?: string;
  fechaHasta?: string;
  liquidado?: boolean;
  pagado?: boolean;
  companyId?: number;
  carpetaId?: number;
}


export const informeApi = createApi({
  reducerPath: 'informeApi',
  baseQuery: customFetchBase,
  tagTypes: ['Informes', 'Carritos'],
  endpoints: (builder) => ({
    obtenerCarritosPaginados: builder.query<GenericResponse, FiltersCarpeta>({
      query: ({ estado, ...rest }) => ({
        url: 'carrito/filtrarCarpetas',
        method: 'POST',
        body: {
          estadoGeneral: estado,
          ...rest,
        },
      }),
      providesTags: [{ type: 'Carritos', id: 'LIST' }],
    }),
    obtenerQueryCarritos: builder.query<GenericResponse, Partial<IQueryCarritos>>({
      query: (query) => ({
        url: 'carrito/query',
        method: 'GET',
        params: query,
      }),
    }),
    obtenerCarritosUserLogueado: builder.query<GenericResponse, { desde: number, hasta: number }>({
      query: ({ desde, hasta }) => ({
        url: 'carrito/getCarritosJWT',
        method: 'GET',
        params: {
          desde,
          hasta,
        },
      }),
      providesTags: [{ type: 'Informes', id: 'LIST' }],
    }),
    liquidarCarrito: builder.mutation<any, { id: number, flag: boolean, fecha: any }>({
      query: ({ id, flag, fecha }) => ({
        url: 'carrito/liquidarCarrito',
        method: 'PUT',
        params: {
          idCarrito: id,
          conIva: flag,
          fecha,
        },
      }),
      invalidatesTags: [{ type: 'Carritos', id: 'LIST' }],
    }),
    liquidarCarritos: builder.mutation<any, { ids: number[], flag: boolean, porEmpresa: boolean, fecha: any }>({
      query: ({ ids, flag, porEmpresa, fecha }) => ({
        url: 'carrito/liquidarCarritos',
        method: 'POST',
        body: {
          listaDevuelta: ids,
          bool: flag,
          porEmpresa,
          fecha
        },
      }),
      invalidatesTags: [{ type: 'Carritos', id: 'LIST' }],
    }),
    liquidarInformesPorUsuario: builder.mutation<any, LiquidarInformesArgs>({
      query: (flags) => ({
        url: 'informe/liquidarFilterInformes',
        method: 'POST',
        body: flags
      }),
      invalidatesTags: [{ type: 'Carritos', id: 'LIST' }],

    }),
    liquidarInformes: builder.mutation<any, LiquidarInformesArgs>({
      query: (args) => ({
        url: 'informe/liquidarInformes',
        method: 'PUT',
        body: args
      }),
      invalidatesTags: [{ type: 'Carritos', id: 'LIST' }],
    }),
    obtenerPDFLiquidacion: builder.mutation<any, number>({
      query: (id) => ({
        url: 'informe/liquidacion',
        method: 'GET',
        params: {
          informeId: id,
        }
      }),
    }),
    obtenerPDFRechazado: builder.mutation<any, number>({
      query: (id) => ({
        url: 'informe/rejected',
        method: 'GET',
        params: {
          informeId: id,
        }
      }),
    }),
    obtenerInformeEntregadoGobierno: builder.mutation<any, number>({
      query: (id) => ({
        url: 'informe/informado',
        method: 'GET',
        params: {
          informeId: id,
        }
      }),
    }),
    pagarCarritos: builder.mutation<GenericResponse, number[]>({
      query: (ids) => ({
        url: 'carrito/pagarCarrito',
        method: 'PUT',
        body: JSON.stringify(ids),
        headers: {
          'Content-Type': 'application/json'
        },
      }),
    }),
    agruparCarritos: builder.mutation<GenericResponse, any>({
      query: (dto) => ({
        url: 'carrito/regroup',
        method: 'POST',
        body: dto,
      }),
    }),
    createInformes: builder.mutation<any, { carrito: IInforme[], nombreCarrito: String }>({
      query: ({ carrito, nombreCarrito }) => ({
        url: `informe/create?nombreCarrito=${nombreCarrito}`,
        method: 'POST',
        body: carrito,
      }),
      invalidatesTags: [{ type: 'Informes', id: 'LIST' }],
    }),
    createInformesAsUser: builder.mutation<any, { carrito: IInforme[], nombreCarrito: String, userId: string | number }>({
      query: ({ carrito, nombreCarrito, userId }) => ({
        url: `informe/CreateInformeAsAUser?nombreCarrito=${nombreCarrito}&idUsuarioParam=${userId}`,
        method: 'POST',
        body: carrito,
      }),
      invalidatesTags: [{ type: 'Informes', id: 'LIST' }],
    }),
    obtenerInformes: builder.query<GenericResponse, void>({
      query: () => ({
        url: 'carrito/getAll',
        method: 'GET',
      }),
      providesTags: [{ type: 'Informes', id: 'LIST' }],
    }),
    obtenerInformePorId: builder.query<IInforme, number>({
      query: (id) => ({
        url: 'informe/informeById',
        method: 'GET',
        params: {
          id: id,
        },
      })
    }),
    editarInforme: builder.mutation<any, IInforme>({
      query: (informe) => ({
        url: `informe/updateInforme?id=${informe.id}`,
        method: 'PUT',
        body: { ...filterKeys(informe) }
      }),
      invalidatesTags: [{ type: 'Informes', id: 'LIST' }],
    }),
    obtenerInformesFiltrados: builder.query<IInforme[], any>({
      query: (filtro) => ({
        url: 'informe/getInformesFiltrados',
        method: 'POST',
        body: filtro,
      }),
      transformResponse: (response: GenericResponse) => {
        const rawInformes = response.data.informes;
        let informes: IInforme[] = rawInformes.map(({ informe, usuario }: { informe: IInforme, usuario: IUser }) =>
          ({ ...informe, usuario })
        );
        return informes;
      },
    }),
    obtenerCarpetasFiltradas: builder.query<GenericResponse, any>({
      query: (filtradoCarpetasDTO) => ({
        url: 'carrito/filtrarCarpetas',
        method: 'POST',
        body: filtradoCarpetasDTO,
      })
    }),
    subirArchivos: builder.mutation<any, FormData>({
      query: (informes) => ({
        url: 'archivo/subirAchivoCrearInforme',
        method: 'POST',
        body: informes,
      }),
      invalidatesTags: [{ type: 'Informes', id: 'LIST' }],
    }),
    obtenerInformesReciclados: builder.query<IInforme[], { desde: number, hasta: number }>({
      query: (params) => ({
        url: 'informe/obtenerInformesReciclados',
        method: 'GET',
        params,
      }),
      providesTags: [{ type: 'Informes', id: 'LIST' }],
      transformResponse: (response: GenericResponse) => {
        const informesYUsuarios = response.data.informes;
        const listaInformes = []

        for (const informeYUsuario of informesYUsuarios) {
          const informe = informeYUsuario.informe;
          listaInformes.push({ ...informe, usuario: informeYUsuario.usuarioAscociado });
        }


        return listaInformes as IInforme[];

      },
    }),
    borrarInforme: builder.mutation<any, number>({
      query: (idInforme) => ({
        url: `informe/reciclarInformes`,
        method: 'POST',
        body: [idInforme]
      }),
      invalidatesTags: [{ type: 'Informes', id: 'LIST' }],
    }),
    rechazarInforme: builder.mutation<any, { id: number, rejectedReason: string, fecha: any }>({
      query: ({ id, rejectedReason, fecha }) => ({
        url: `informe/rejected`,
        method: 'PUT',
        params: {
          id,
          rejectedReason,
          fecha,
          template: 'REJECTED_INFORME',
        }
      }),
      invalidatesTags: [{ type: 'Informes', id: 'LIST' }],
    }),
    observarCarpeta: builder.mutation<any, { id: number, observedReason: string, fecha: any }>({
      query: ({ id, observedReason, fecha }) => ({
        url: `carrito/observed`,
        method: 'PUT',
        params: {
          id,
          observedReason,
          fecha,
          template: 'OBSERVED_CARRITO',
        }
      }),
      invalidatesTags: [{ type: 'Informes', id: 'LIST' }],
    }),
    restaurarInformes: builder.mutation<any, number>({
      query: (idInforme) => ({
        url: `informe/recuperarInformes`,
        method: 'PUT',
        body: [idInforme]
      }),
      invalidatesTags: [{ type: 'Informes', id: 'LIST' }],
    }),
    obtenerVelocidadPorInforme: builder.query<any, string>({
      query: (keyInforme) => ({
        url: `informe/getVelocidadesInforme?informeKeyPrecio=${keyInforme}`,
        method: 'GET',
      }),
    }),
    agregarInformado: builder.mutation<any, { idInforme: IInforme['id'], archivo: File, fecha: any }>({
      query: ({ idInforme, archivo, fecha }) => {
        const formData = new FormData();
        formData.append('archivo', archivo);

        return {
          url: 'archivo/aniadirInformado',
          method: 'POST',
          body: formData,
          params: { idInforme, fecha },

        };
      },
    }),
    obtenerCarritoPorId: builder.query<any, number>({
      query: (carritoId) => ({
        url: `carrito/obtenerCarritoId?carritoId=${carritoId}`,
        method: 'GET',
      }),
    }),
    obtenerLogsErrores: builder.query<{ logs: IInformeLog[], total: number }, { desde: number, hasta: number }>({
      query: (params) => ({
        url: 'log/getLogs',
        method: 'GET',
        params,
      }),
      transformResponse: ({ data }: GenericResponse) => {
        const logs = data.result;
        const total = data.totalLogs;
        return { logs, total };
      },
    }),
    actualizarEntrada: builder.mutation<any, { id: number, entrada: string }>({
      query: ({ id, entrada }) => ({
        url: `informe/setIdEntrada`,
        method: 'POST',
        body: {
          informeId: id,
          entrada,
        },
      }),
    }),
    actualizarNombreCarrito: builder.mutation<any, { id: number, nombre?: string | null }>({
      query: ({ id, nombre }) => ({
        url: `carrito/name`,
        method: 'POST',
        params: {
          id,
          nombre,
        },
      }),
    }),
    borrarCarrito: builder.mutation<any, number>({
      query: (idCarrito) => ({
        url: 'carrito',
        method: 'DELETE',
        body: {
          id: idCarrito,
        },
      }),
      invalidatesTags: [{ type: 'Carritos', id: 'LIST' }],
    }),
    imprimirInforme: builder.mutation<any, { id: number, printed: boolean }>({
      query: ({ id, printed }) => ({
        url: 'informe/print',
        method: 'PUT',
        params: {
          id,
          printed,
        },
      }),
    }),
    eliminarInformado: builder.mutation<any, number>({
      query: (idInforme) => ({
        url: `archivo/eliminarInformado/${idInforme}`,
        method: 'DELETE',
      }),

    }),
  }),
});

export const {
  useObtenerCarritosPaginadosQuery,
  useObtenerQueryCarritosQuery,
  useObtenerCarritosUserLogueadoQuery,
  useLiquidarCarritoMutation,
  useLiquidarCarritosMutation,
  useLiquidarInformesPorUsuarioMutation,
  useLiquidarInformesMutation,
  useObtenerPDFLiquidacionMutation,
  useObtenerPDFRechazadoMutation,
  useObtenerInformeEntregadoGobiernoMutation,
  usePagarCarritosMutation,
  useAgruparCarritosMutation,
  useCreateInformesMutation,
  useCreateInformesAsUserMutation,
  useObtenerInformesQuery,
  useObtenerInformePorIdQuery,
  useEditarInformeMutation,
  useObtenerInformesFiltradosQuery,
  useSubirArchivosMutation,
  useObtenerInformesRecicladosQuery,
  useBorrarInformeMutation,
  useRechazarInformeMutation,
  useObservarCarpetaMutation,
  useRestaurarInformesMutation,
  useObtenerVelocidadPorInformeQuery,
  useAgregarInformadoMutation,
  useObtenerCarritoPorIdQuery,
  useObtenerLogsErroresQuery,
  useActualizarEntradaMutation,
  useActualizarNombreCarritoMutation,
  useBorrarCarritoMutation,
  useImprimirInformeMutation,
  useEliminarInformadoMutation,
} = informeApi;


export const useObtenerCarritosLazyQuery = informeApi.endpoints.obtenerQueryCarritos.useLazyQuery;
